import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  ApprovalRequest,
  CreateApprovalPayload,
} from '../types/approval';
import { DEFAULT_PAGE_SIZE, PageResponse } from '../types/page';

export type ApprovalListParams = {
  page?: number;
  size?: number;
  q?: string;
};

function listParams(params?: ApprovalListParams) {
  return {
    page: params?.page ?? 0,
    size: params?.size ?? DEFAULT_PAGE_SIZE,
    ...(params?.q?.trim() ? { q: params.q.trim() } : {}),
  };
}

export async function createApproval(payload: CreateApprovalPayload): Promise<ApprovalRequest> {
  const { data } = await apiClient.post<ApprovalRequest>('/approvals', payload);
  return data;
}

export async function fetchSentApprovals(
  params?: ApprovalListParams
): Promise<PageResponse<ApprovalRequest>> {
  const { data } = await apiClient.get<PageResponse<ApprovalRequest>>('/approvals/sent', {
    params: listParams(params),
  });
  return data;
}

export async function fetchInboxApprovals(
  params?: ApprovalListParams
): Promise<PageResponse<ApprovalRequest>> {
  const { data } = await apiClient.get<PageResponse<ApprovalRequest>>('/approvals/inbox', {
    params: listParams(params),
  });
  return data;
}

/** 결재 대기함 건수(헤더 뱃지). 목록과 동일 기준(대기 문서 수) */
export async function fetchInboxApprovalCount(): Promise<number> {
  try {
    const { data } = await apiClient.get<{ count?: number | string }>('/approvals/inbox/count');
    const raw = data?.count;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(n) && n >= 0) {
      return Math.floor(n);
    }
  } catch {
    // count API 실패 시 목록 totalElements로 대체
  }
  const list = await fetchInboxApprovals({ page: 0, size: 1 });
  return list?.totalElements ?? 0;
}

/** 내가 기안했거나 결재선에 포함된 문서(완료·반려 포함, CANCELLED 제외) */
export async function fetchMineApprovals(
  params?: ApprovalListParams
): Promise<PageResponse<ApprovalRequest>> {
  const { data } = await apiClient.get<PageResponse<ApprovalRequest>>('/approvals/mine', {
    params: listParams(params),
  });
  return data;
}

export async function fetchApproval(requestId: number): Promise<ApprovalRequest> {
  const { data } = await apiClient.get<ApprovalRequest>(`/approvals/${requestId}`);
  return data;
}

export async function approveApprovalStep(
  requestId: number,
  stepId: number,
  comment?: string
): Promise<ApprovalRequest> {
  const { data } = await apiClient.post<ApprovalRequest>(
    `/approvals/${requestId}/steps/${stepId}/approve`,
    { comment: comment || null }
  );
  return data;
}

export async function rejectApprovalStep(
  requestId: number,
  stepId: number,
  comment?: string
): Promise<ApprovalRequest> {
  const { data } = await apiClient.post<ApprovalRequest>(
    `/approvals/${requestId}/steps/${stepId}/reject`,
    { comment: comment || null }
  );
  return data;
}

export async function cancelApproval(requestId: number): Promise<ApprovalRequest> {
  const { data } = await apiClient.post<ApprovalRequest>(`/approvals/${requestId}/cancel`);
  return data;
}

export async function downloadApprovalPdf(
  requestId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/approvals/${requestId}/pdf`,
    fileName || `결재문서_${requestId}.pdf`
  );
}
